import { DodonaElement } from "@/components/meta/dodona_element";
import { showInlineFile, showRealFile } from "@/file_viewer";
import type { FileEntry } from "@/types/FeedbackResult";
import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * Renders an inline link to a file in the feedback table.
 *
 * @element d-feedback-file-link
 *
 * @property {FileEntry["location"]} location Where the file is.
 * @property {string} name The name of the file to render.
 * @property {string} content Url or inline contents, depending on the location.
 * @property {string} activityPath The path to the activity.
 * @property {string} accessToken The access token to use for the file viewer.
 */
@customElement("d-feedback-file-link")
export class FeedbackFileLink extends DodonaElement {
    @property({ type: String }) location!: FileEntry["location"];
    @property({ type: String }) name!: string;
    @property({ type: String }) content!: string;

    @property({ type: String }) activityPath: string = "";
    @property({ type: String }) accessToken: string | null = null;

    private async onClickLink(e: Event): Promise<void> {
        e.preventDefault();

        if (this.location === "inline") {
            showInlineFile(this.name, this.content);
        } else if (this.location === "href") {
            const content = contentWithToken(this.content, this.location, this.accessToken);
            await showRealFile(this.name, this.activityPath, content);
        }
    }

    render(): TemplateResult {
        // Add a real link if the entry has a location.
        const path = this.location === "href" ? contentWithToken(this.content, this.location, this.accessToken) : "#";
        return html`<a href="${path}" @click=${this.onClickLink}>${this.name}</a>`;
    }
}

export function contentWithToken(content: string, location: FileEntry["location"], accessToken: string | null): string {
    if (accessToken && location === "href") {
        return `${content}?token=${encodeURIComponent(accessToken)}`;
    }

    return content;
}
